import { createSelector } from 'reselect';
import { call, select } from 'redux-saga/effects';
import { get, logMessage, pageContext } from 'ctm-util-functions';
import cloneDeep from 'lodash.clonedeep';

import {
    errorTrackingSelector,
    getProductIdsByAvailability,
    productInfoSelector,
    quoteIdSelector,
    resultsConfigSelector,
    selectedProductSelector,
    sortedAndFilteredProductsSelector,
    trackQuoteFormDataSelector,
    transactionPayloadSelector,
    verticalNameSelector,
    lastViewedPageNameSelector,
    splitTrafficIdSelector,
    quoteFormSelector,
    ecommerceDataSelector,
    pageNameSelector
} from 'ctm-selectors';
import { getPrefillProgress } from 'ctm-selectors/selectors/tracking/tracking';
import { isRebuildCalculatorEnabled, isContentsCalculatorEnabled } from 'ctm-components/components/feature_flags_utils';
import { getOfferDetails } from './state_selectors';
import { objectValuesToLowerCase } from './utils';
import { reporterLog } from './reporter';

/**
 * Stores a deferred page-view so the original landing page can be replayed once
 * journey transaction data becomes available.
 * @type {{ actionType: string, pageName: string, data: Object }|null}
 */
let _pendingPageView = null;

// Exported for testing only
export const _test_getPendingPageView = () => _pendingPageView;
export const _test_clearPendingPageView = () => { _pendingPageView = null; };



/**
 * Get the ProductCategory state.
 * @param  {Object} state   - The application Redux store.
 * @returns {Object}         - The Product Category state.
 */
const productCategorySelector = (state) => {
    const productCategory = get(pageContext.getProps(), 'verticalSelectors.getProductCategory', false);
    if (productCategory) {
        return productCategory(state);
    }
    return null;
};

/**
 * HOF that creates a dynamic selector that takes extra arguments
 * @param {String} event - The name of the event to be triggered.
 * @param {String} eventType - ctm_event_type for the tracking.
 * @param {String} eventAction - ecommerce action, 'detail' or 'click'
 * @param {String} productId - the productId from the action for the product we are tracking
 * @returns {Function} - a reselect function that gets the tracking data for product call to action click
 */
const getTrackProductImpressionData = (event, eventType, eventAction, productId) => {
    return createSelector(
        [verticalNameSelector, productCategorySelector, selectedProductSelector, sortedAndFilteredProductsSelector, productInfoSelector],
        (verticalName, productCategory, selectedProduct, sortedAndFilteredProducts, productInfo) => {
            // If productId is present, use it to get the desired product for tracking, otherwise use selectedProductSelector
            if (productId) {
                // eslint-disable-next-line no-param-reassign
                selectedProduct = sortedAndFilteredProducts
                    .filter((product) => {
                        return product.getIn(['productMeta', 'id']) === productId;
                    })
                    .get(0);
            }
            const verticalTrackingSelectors = get(pageContext.getProps(), 'verticalSelectors.tracking');
            const verticalEcommerceProductValues = verticalTrackingSelectors ? verticalTrackingSelectors.ecommerce(selectedProduct) : {};
            const productName = selectedProduct.get('productName');
            const providerName = selectedProduct.get('providerName');
            return {
                event,
                ecommerce: {
                    ctm_event_type: eventType,
                    [eventAction]: {
                        actionField: {
                            list: verticalName
                        },
                        products: [
                            {
                                id: selectedProduct.getIn(['productMeta', 'id']),
                                name: productName && providerName ? `${providerName} - ${productName}` : null,
                                position: sortedAndFilteredProducts.indexOf(selectedProduct) + 1,
                                brand: selectedProduct.get('providerCode') || null,
                                category: productCategory,
                                ...verticalEcommerceProductValues
                            }
                        ]
                    }
                }
            };
        }
    );
};

const getHandoverClickProduct = createSelector(
    [verticalNameSelector, productCategorySelector, sortedAndFilteredProductsSelector, productInfoSelector],
    (verticalName, productCategory, sortedAndFilteredProducts, productInfo) => {
        const product = productInfo && productInfo.get ? productInfo.get('product') : null;
        const productId = product && product.get ? product.get('productId') : null;
        const selectedProduct = productId
            ? sortedAndFilteredProducts.find((result) => result.getIn(['productMeta', 'id']) === productId)
            : null;

        if (selectedProduct) {
            const verticalTrackingSelectors = get(pageContext.getProps(), 'verticalSelectors.tracking');
            const verticalEcommerceProductValues = verticalTrackingSelectors ? verticalTrackingSelectors.ecommerce(selectedProduct) : {};
            const productName = selectedProduct.get('productName');
            const providerName = selectedProduct.get('providerName');

            return {
                actionField: {
                    list: verticalName
                },
                products: [
                    {
                        id: selectedProduct.getIn(['productMeta', 'id']),
                        name: productName && providerName ? `${providerName} - ${productName}` : null,
                        position: sortedAndFilteredProducts.indexOf(selectedProduct) + 1,
                        brand: selectedProduct.get('providerCode') || null,
                        category: productCategory,
                        ...verticalEcommerceProductValues
                    }
                ]
            };
        }

        const fallbackName = product && product.get ? product.get('productName') : null;
        const fallbackProviderName = product && product.get ? product.get('providerName') : null;
        const fallbackBrand = product && product.get ? product.get('providerCode') : null;

        return {
            actionField: {
                list: verticalName
            },
            products: productId ? [{
                id: productId,
                name: fallbackName && fallbackProviderName ? `${fallbackProviderName} - ${fallbackName}` : null,
                brand: fallbackBrand || null,
                category: productCategory
            }] : []
        };
    }
);

/**
 * Reselect selector for getting data to track handover.
 * Builds the payload from current Redux state first, with no dependency on localStorage.
 * Returns quoteUrl and productId so the saga can schedule the redirect independently of tracking.
 */
export const getHandoverData = createSelector(
    [verticalNameSelector, productCategorySelector, trackQuoteFormDataSelector, transactionPayloadSelector, productInfoSelector, getHandoverClickProduct],
    (vertical, verticalFilter, formData, transaction, productInfo, clickData) => {
        const product = productInfo && productInfo.get ? productInfo.get('product') : null;
        const quoteUrl = product && product.get ? product.get('quoteUrl') : null;
        const productId = product && product.get ? product.get('productId') : null;

        return {
            event: 'PAGE_VIEW_EVENT',
            vertical,
            verticalFilter,
            transaction: transaction ? transaction.toJS() : null,
            ...formData,
            quoteUrl,
            productId,
            ecommerce: {
                click: clickData
            }
        };
    }
);

const trackMSATSDataRetrievalStatusSelector = createSelector(
    [quoteFormSelector, quoteIdSelector, transactionPayloadSelector],
    (quoteForm, quoteId, transaction) => {
        const results = {
            meterSource: quoteForm.getIn(['data', 'electricityMeterDetails', 'source']),
            nmiDataRetrievalStatus: quoteForm.getIn(['data', 'nmiDataRetrievalStatus'])
        };

        if (quoteId) {
            results.quoteId = quoteId;
        }

        return {
            transaction: transaction && transaction.toJS(),
            results
        };
    }
);

/**
 * Helper method to retrieve transaction object off payload.
 * @param {Immutable.Map} state The app state
 * @returns {Immutable.Map|false} Transaction data
 * @private
 */
const _getTransactionPayload = (state) => {
    const transaction = transactionPayloadSelector(state);
    return transaction && transaction.toJS();
};

/**
 * @desc Get transaction data including the event.
 * @param {Object} state    - The application state.
 * @param {String} event    - The name of the event to be triggered.
 * @returns {Object} trackingData to be pushed to GTM.
 */
const getTransactionData = (state, event) => {
    const settings = pageContext.getProps().models.pageSchemaModel.getSettings();
    const vertical = get(settings, 'tracking.vertical', null);
    return { event, vertical, transaction: _getTransactionPayload(state) };
};

/**
 * @desc Get Page data including the event.
 * @param {Object} state    - The application state
 * @param {String} event    - The name of the event to be triggered
 * @param {String} actionStep - An optional actionStep (to override default) to include in the GTM event
 * @returns {Object} trackingData to be pushed to GTM.
 */
const getPageViewData = (state, event, actionStep = '') => {
    const settings = pageContext.getProps().models.pageSchemaModel.getSettings();
    const vertical = get(settings, 'tracking.vertical', null);
    const verticalFilter = productCategorySelector(state);
    const transaction = _getTransactionPayload(state);
    const form = quoteFormSelector(state);
    const formData = trackQuoteFormDataSelector(state);
    const pageName = pageNameSelector(state);
    const hasPortableItems = quoteFormSelector(state).getIn(['data', 'hasPortableItems']);
    const serverConfig = state.get('serverConfig');
    const formJS = form ? form.toJS() : {};

    if (actionStep !== '') {
        formData.actionStep = vertical + actionStep;
    }

    // Add rebuildCostCalculatorData to GTM tracking only for specific pages
    if (isRebuildCalculatorEnabled(serverConfig) && (pageName === 'personal_effects' || (pageName === 'previous_cover' && hasPortableItems !== 'Y'))) {
        try {
            if (formData.journey) {
                const rebuildCostCalculatorData = get(formJS, 'rebuildCostCalculator.apiOnLoadResponse');
                const totalRebuildCost = get(formJS, 'coverDetails.totalRebuildCost');
                const prefillAttempt = form.getIn(['helpers', 'prefill', 'attempt']) || 0;
                // Valid rebuild cost statuses for reporting
                const RebuildCostCalculatorStatus = {
                    Below: 'below',
                    Within: 'within',
                    Above: 'above',
                    Unavailable: 'unavailable',
                    AnonymousPrefill: 'anonymous prefill',
                    ExceededCtmThreshold: 'exceeded ctm threshold',
                    UnderCtmThreshold: 'under ctm threshold'
                };

                let calculatedStatus;

                if (rebuildCostCalculatorData && rebuildCostCalculatorData.low && rebuildCostCalculatorData.high && rebuildCostCalculatorData.medium) {
                    if (rebuildCostCalculatorData.low < serverConfig.get('homeRebuildMinThreshold')) {
                        calculatedStatus = RebuildCostCalculatorStatus.UnderCtmThreshold;
                    } else if (rebuildCostCalculatorData.high > serverConfig.get('homeRebuildMaxThreshold')) {
                        calculatedStatus = RebuildCostCalculatorStatus.ExceededCtmThreshold;
                    } else if (totalRebuildCost < rebuildCostCalculatorData.low) {
                        calculatedStatus = RebuildCostCalculatorStatus.Below;
                    } else if (totalRebuildCost > rebuildCostCalculatorData.high) {
                        calculatedStatus = RebuildCostCalculatorStatus.Above;
                    } else {
                        calculatedStatus = RebuildCostCalculatorStatus.Within;
                    }
                } else if (prefillAttempt > 0) {
                    // The calculator data is unavailable due to anonymous prefill populating the cover details without the calculator
                    calculatedStatus = RebuildCostCalculatorStatus.AnonymousPrefill;
                } else {
                    // The calculator data is unavailable due to an error or missing gnaf
                    calculatedStatus = RebuildCostCalculatorStatus.Unavailable;
                }

                if (!window.dataLayer) {
                    window.dataLayer = [];
                }
                window.dataLayer.push({
                    event: 'INTERACTION_EVENT',
                    interaction: {
                        ixn_action: 'Input',
                        ixn_type: 'Journey Context',
                        ixn_object: 'H&C Sum Insured Range',
                        ixn_label: calculatedStatus
                    }
                });

                logMessage('interaction_event pushed to GTM:', calculatedStatus);

                window.dataLayer.push({
                    event: 'Clear interaction array',
                    interaction: []
                });
            }
        } catch (e) {
            reporterLog({ context: 'Failed to parse rebuildCostCalculatorData from sessionStorage' }, e);
        }
    }

    // Add contentsCalculatorData to GTM tracking only for specific pages
    if (isContentsCalculatorEnabled(serverConfig) && (pageName === 'personal_effects' || (pageName === 'previous_cover' && hasPortableItems !== 'Y'))) {
        try {
            if (formData.journey) {
                const contentsCostCalculatorData = get(formJS, 'contentsCostCalculator.apiOnLoadResponse');
                const totalContentsCost = get(formJS, 'coverDetails.totalContentReplacementCost');
                const prefillAttempt = form.getIn(['helpers', 'prefill', 'attempt']) || 0;
                const occupancyType = get(formJS, 'occupancy.occupancyType');
                let homeContentsMinThreshold;
                let homeContentsMaxThreshold;

                try {
                    const rulesModel = get(pageContext.getProps(), 'models.rulesModel');
                    const contentsRangeRuleIdentifier = occupancyType === 'LANDLORD'
                        ? 'isValidTotalContentReplacementCostLandlord'
                        : 'isValidTotalContentReplacementCost';
                    const [contentsRangeRule] = rulesModel.extractRules(contentsRangeRuleIdentifier);
                    homeContentsMinThreshold = get(contentsRangeRule, 'schema.filterValue.min');
                    homeContentsMaxThreshold = get(contentsRangeRule, 'schema.filterValue.max');
                } catch (error) {
                    logException(error, 'warn', { context: 'Failed to resolve contents thresholds from rules model' });
                }

                homeContentsMinThreshold = Number(homeContentsMinThreshold);
                homeContentsMaxThreshold = Number(homeContentsMaxThreshold);
                const hasContentsThresholds = Number.isFinite(homeContentsMinThreshold) && Number.isFinite(homeContentsMaxThreshold);

                // Valid contents cost statuses for reporting
                const ContentsCostCalculatorStatus = {
                    Below: 'below',
                    WithinAQ: 'withinAQ',
                    WithinQP: 'withinQP',
                    Above: 'above',
                    Unavailable: 'unavailable',
                    AnonymousPrefill: 'anonymous prefill',
                    ExceededCtmThreshold: 'exceeded ctm threshold',
                    UnderCtmThreshold: 'under ctm threshold'
                };

                let calculatedStatus;

                if (contentsCostCalculatorData && contentsCostCalculatorData.average && contentsCostCalculatorData.quality && contentsCostCalculatorData.prestige) {
                    if (hasContentsThresholds && contentsCostCalculatorData.average < homeContentsMinThreshold) {
                        calculatedStatus = ContentsCostCalculatorStatus.UnderCtmThreshold;
                    } else if (hasContentsThresholds && contentsCostCalculatorData.average > homeContentsMaxThreshold) {
                        calculatedStatus = ContentsCostCalculatorStatus.ExceededCtmThreshold;
                    } else if (totalContentsCost < contentsCostCalculatorData.average) {
                        calculatedStatus = ContentsCostCalculatorStatus.Below;
                    } else if (totalContentsCost > contentsCostCalculatorData.prestige) {
                        calculatedStatus = ContentsCostCalculatorStatus.Above;
                    } else {
                        // This else is a bit lazy Basically we must be between average and prestige
                        // Which simplifies this statement
                        if (totalContentsCost > contentsCostCalculatorData.quality) {
                            calculatedStatus = ContentsCostCalculatorStatus.WithinQP;
                        } else {
                            calculatedStatus = ContentsCostCalculatorStatus.WithinAQ;
                        }
                    }
                } else if (prefillAttempt > 0) {
                    // The calculator data is unavailable due to anonymous prefill populating the cover details without the calculator
                    calculatedStatus = ContentsCostCalculatorStatus.AnonymousPrefill;
                } else {
                    // The calculator data is unavailable due to an error or missing gnaf
                    calculatedStatus = ContentsCostCalculatorStatus.Unavailable;
                }

                if (!window.dataLayer) {
                    window.dataLayer = [];
                }
                window.dataLayer.push({
                    event: 'INTERACTION_EVENT',
                    interaction: {
                        ixn_action: 'Input',
                        ixn_type: 'Journey Context',
                        ixn_object: 'H&C Sum Insured Contents Range',
                        ixn_label: calculatedStatus
                    }
                });

                logMessage('interaction_event pushed to GTM:', calculatedStatus);

                window.dataLayer.push({
                    event: 'Clear interaction array',
                    interaction: []
                });
            }
        } catch (e) {
            logException(e, 'error', { context: 'Failed to parse contentsCostCalculatorData from sessionStorage' });
        }
    }

    return {
        event,
        vertical,
        verticalFilter,
        transaction,
        ...formData
    };
};

const getEcommerceData = (state, type) => {
    return { ...ecommerceDataSelector(type)(state) };
};

/**
 * @desc Get getTrackQuoteComparisonResultsData data including the event.
 * @param {Object} state    - The application state.
 * @param {String} event    - The name of the event to be triggered.
 * @returns {Object} trackingData to be pushed to GTM
 */
const getTrackQuoteComparisonResultsData = (state, event) => {
    const selectedProductIds = resultsConfigSelector(state)
        .getIn(['filters', 'selectedProductIds'])
        .toJS();
    const brands = selectedProductIds.map((id) => (id.indexOf('-') !== -1 ? id.split('-')[0] : id));
    return { event, comparedProducts: { brands, selectedProductIds } };
};

/**
 * @desc Get getTrackQuoteNotProvidedData data including the event and vertical name
 * @param {Object} state    - The application state.
 * @param {String} event    - The name of the event to be triggered.
 * @returns {Object|Boolean} trackingData to be pushed to GTM
 */
const getTrackQuoteNotProvidedData = (state, event) => {
    const unavailableProducts = getProductIdsByAvailability(state, false).toJS();
    if (!unavailableProducts.length) {
        return false;
    }
    return { event, unavailableProducts };
};

/**
 * @desc Get the trackTransactionUpdate data.
 * @param {Object} state    - The application state
 * @param {String} event    - The name of the event to be triggered
 * @returns {Object} trackingData to be pushed to GTM
 */
const getErrorTracking = (state, event) => {
    const errors = errorTrackingSelector(state);
    return { event, errors };
};

/**
 * @desc Get getTrackMSATSDataRetrievalStatus data including the event.
 * @param {Object} state    - The application state.
 * @param {String} event    - The name of the event to be triggered.
 * @returns {Object} trackingData to be pushed to GTM
 */
const getTrackMSATSDataRetrievalStatus = (state, event) => {
    const settings = pageContext.getProps().models.pageSchemaModel.getSettings();
    const vertical = get(settings, 'tracking.vertical', null);
    return { event, vertical, ...trackMSATSDataRetrievalStatusSelector(state) };
};

/**
 * Push the data to the Google Tag Manager.
 * More info in here: https://developers.google.com/tag-manager/quickstart
 * @param {Object} action The action details.
 * @param {Object} data The data to be pushed to google data layer.
 */
const recordGTM = function* (action, { event, ...data }) {
    // Initialise the array the same way GTM itself does so events are always
    // captured even when the GTM script hasn't loaded (e.g. local dev, ad-blockers).
    window.dataLayer = window.dataLayer || [];
    const lowerCaseData = { event, ...objectValuesToLowerCase(cloneDeep(data)) };
    try {
        window.dataLayer.push(lowerCaseData);
    } catch (error) {
        Object.assign(error, { lowerCaseData });
        yield call(reporterLog, action, error);
    }
};

/**
 * @desc generator to catch all track actions, then get different data from selectors, then push to the dataLayer
 * @param {Object} action The dispatched action that contains the description of how to track data
 * @returns {Boolean} true if track successfully, otherwise false
 */
export const track = function* (action) {
    try {
        let data = null;
        let event = '';

        switch (action.type) {
            // 'SET_JOURNEY_PAYLOAD' = Whenever journey/transaction data gets updated
            case 'SET_JOURNEY_PAYLOAD': {
                event = 'trackTransactionUpdate';
                data = yield select(getTransactionData, event);
                break;
            }
            // 'SET_ROUTER_LOCATION' = Save on all route changes on "journey" pages
            case 'TRACK_INITIAL_FORM':
            case 'SET_RESULTS_PAYLOAD':
            case 'SET_RESULTS_PAYLOAD_HNC':
            case 'SET_ROUTER_LOCATION': {
                // A new route or TRACK_INITIAL_FORM supersedes any previously deferred page view.
                // Clearing synchronously here (before the first yield) lets the SET_JOURNEY_PAYLOAD
                // replay block see null after its yield delay(0) and skip the replay.
                if (action.type === 'SET_ROUTER_LOCATION' || action.type === 'TRACK_INITIAL_FORM') {
                    _pendingPageView = null;
                }

                const pageName = yield select(pageNameSelector);
                const verticalName = yield select(verticalNameSelector);
                const lastViewedPageName = yield select(lastViewedPageNameSelector);

                if (lastViewedPageName === 'welcome_back' && pageName === 'car_colour') { return false; }

                // avoid triggering the page view event twice on the car verify page
                // avoid triggering the page view event twice on the car verify page
                if (verticalName === 'car' && pageName === 'verify') return false;

                // Only fire an event on the results page if we've actually fetched the results.
                // Special case for H&C as there is additional logic on the SET_RESULTS_PAYLOAD_HNC action.
                if (pageContext.getVertical() === 'home-contents-insurance'
                    ? pageName === 'results' && action.type !== 'SET_RESULTS_PAYLOAD_HNC'
                    : pageName === 'results' && action.type !== 'SET_RESULTS_PAYLOAD'
                ) return false;
                event = 'PAGE_VIEW_EVENT';
                const pageData = yield select(getPageViewData, event);
                const ecommerceData = yield select(getEcommerceData, action.type);
                data = { ...pageData, ...ecommerceData };

                if (pageName === 'welcome_back') {
                    const prefill = yield select(getPrefillProgress);
                    data = { ...data, prefill };
                }

                if (pageName !== 'handover') {
                    localStorage.setItem('trackingData', JSON.stringify(data));
                }

                // on first page load, the transaction details won't be ready yet so we don't want to track the event yet
                // it will be replayed after SET_JOURNEY_PAYLOAD once transaction data is available
                const transaction = yield select(transactionPayloadSelector);
                // Exception for anonymous prefill pages as no journey (transaction object) exists at this point
                // Handover is excluded: its PAGE_VIEW_EVENT is always fired via HANDOVER_TO_PARTNER,
                // so deferring it here would cause a duplicate event when the transaction arrives.
                if (!transaction && action.name !== 'prefill_check') {
                    if (pageName !== 'handover') {
                        _pendingPageView = {
                            actionType: action.type,
                            pageName,
                            data: cloneDeep(data)
                        };
                    }
                    return false;
                }
                break;
            }
            // 'TRACK_UNAVAILABLE_QUOTES' = When retrieving results successfully and there are no quotes.
            case 'TRACK_UNAVAILABLE_QUOTES': {
                event = 'trackQuoteNotProvided';
                data = yield select(getTrackQuoteNotProvidedData, event);
                // If they're all available, don't run this call.
                if (data === false) {
                    return false;
                }
                break;
            }
            case 'TOGGLE_COMPARE_MODE': {
                if (action.toggle === true) {
                    event = 'trackQuoteComparison';
                    data = yield select(getTrackQuoteComparisonResultsData, event);
                } else {
                    return false;
                }
                break;
            }

            // 'VALIDATE_PAGE' = When the page is being validated
            case 'VALIDATE_PAGE': {
                event = 'errorTracking';
                data = yield select(getErrorTracking, event);
                if (data.errors.length === 0) return false;
                break;
            }
            case 'CALL_DIRECT':
            case 'TRACK_CALL_ME_BACK': {
                event = 'PAGE_VIEW_EVENT';
                const productId = get(action, 'requestPayload.productId', action.productId);
                const trackProductImpressionData = yield select(getTrackProductImpressionData(event, action.type.replace('TRACK_', ''), 'click', productId));
                const verticalName = yield select(verticalNameSelector);
                data = { actionStep: `${verticalName} handover`, ...trackProductImpressionData };
                break;
            }
            // 'POSTCODE_NOT_SUPPORTED' = when user enters a postcode that TWLD does not support
            case 'POSTCODE_NOT_SUPPORTED': {
                event = 'trackQuoteEvent';
                data = { event, action: 'location not available' };
                break;
            }
            case 'HANDOVER_TO_PARTNER': {
                event = 'PAGE_VIEW_EVENT';
                const productInfo = yield select(productInfoSelector);
                const handoverUrl = productInfo && productInfo.getIn ? productInfo.getIn(['product', 'quoteUrl']) : null;
                const handoverProductId = productInfo && productInfo.getIn ? productInfo.getIn(['product', 'productId']) : null;
                // Schedule redirect before any fragile tracking work so handover cannot be blocked by GTM failures.
                setTimeout(() => {
                    if (handoverUrl) {
                        window.location.replace(handoverUrl);
                    }
                }, 4500);

                try {
                    const handoverData = yield select(getHandoverData);
                    // quoteUrl and productId drive the redirect ÔÇö strip them before the GTM push.
                    const { quoteUrl, productId, ...handoverGtmData } = handoverData;
                    yield call(recordGTM, action, handoverGtmData);
                    yield call(logMessage, `${event} pushed to GTM`, handoverGtmData);
                } catch (error) {
                    yield call(reporterLog, action, error);
                }

                return true;
            }
            // 'TRACK_MSATS_RETRIEVAL_STATUS' = when MSATS data retrieval status in results when MSATS is available
            case 'TRACK_MSATS_RETRIEVAL_STATUS': {
                // do not track the status if not in MSATS a/b testing test version
                const splitTrafficId = yield select(splitTrafficIdSelector);

                if (splitTrafficId !== 'msats') {
                    return false;
                }

                event = 'trackNmiDataRetrieval';
                data = yield select(getTrackMSATSDataRetrievalStatus, event);
                break;
            }
            case 'OFFER_SHOWN': {
                event = 'OFFER_EVENT';
                const offerDetailsFromPayload = yield select(getOfferDetails);
                const offerDetails = offerDetailsFromPayload.offers[0].offer;
                // RPO banner to fire dispatch once RPO banner is mounted;
                data = { event, offerDetails };
                break;
            }
            case 'ELEVEN_MONTH_QUOTE_START': {
                event = 'PAGE_VIEW_EVENT';
                const pageData = yield select(getPageViewData, event, ' quote start');
                const ecommerceData = yield select(getEcommerceData, action.type);
                data = { ...pageData, ...ecommerceData };
                const transaction = yield select(transactionPayloadSelector);
                if (!transaction) return false;
                break;
            }
            case 'ELEVEN_MONTH_RESULTS': {
                event = 'PAGE_VIEW_EVENT';
                const pageData = yield select(getPageViewData, event, ' results');
                const ecommerceData = yield select(getEcommerceData, action.type);
                data = { ...pageData, ...ecommerceData };
                const transaction = yield select(transactionPayloadSelector);
                if (!transaction) return false;
                break;
            }
            case 'ELEVEN_MONTH_VERIFY': {
                const verticalName = yield select(verticalNameSelector);
                const pageName = yield select(pageNameSelector);
                data = {
                    event: 'PAGE_VIEW_EVENT',
                    transaction: { journeyId: action.journeyId },
                    vertical: verticalName,
                    actionStep: `${verticalName} ${pageName}`
                };
                break;
            }
            case 'PREFILL_VERIFY_FAIL': {
                const verticalName = yield select(verticalNameSelector);
                data = {
                    event: 'PAGE_VIEW_EVENT',
                    vertical: verticalName,
                    actionStep: `${verticalName} prefill verify failed`
                };
                break;
            }
            case 'PREFILL_LOCKOUT': {
                const verticalName = yield select(verticalNameSelector);
                data = {
                    event: 'PAGE_VIEW_EVENT',
                    vertical: verticalName,
                    actionStep: `${verticalName} prefill lockout`
                };
                break;
            }
            case 'PREFILL_CHECK_SUCCESS': {
                const verticalName = yield select(verticalNameSelector);
                data = {
                    event: 'PAGE_VIEW_EVENT',
                    vertical: verticalName,
                    actionStep: `${verticalName} prefill check success`
                };
                break;
            }
            case 'PREFILL_CHECK_FAILED': {
                const verticalName = yield select(verticalNameSelector);
                data = {
                    event: 'PAGE_VIEW_EVENT',
                    vertical: verticalName,
                    actionStep: `${verticalName} prefill check failed`
                };
                break;
            }
            case 'PREFILL_QUOTE_START': {
                event = 'PAGE_VIEW_EVENT';
                const pageData = yield select(getPageViewData, event, ' quote start');
                const ecommerceData = yield select(getEcommerceData, action.type);
                data = { ...pageData, ...ecommerceData };
                const transaction = yield select(transactionPayloadSelector);
                if (!transaction) return false;
                break;
            }
            case 'PREFILL_SAVED_RESULTS': {
                event = 'PAGE_VIEW_EVENT';
                const pageData = yield select(getPageViewData, event, ' results');
                const ecommerceData = yield select(getEcommerceData, action.type);
                data = { ...pageData, ...ecommerceData };
                const transaction = yield select(transactionPayloadSelector);
                if (!transaction) return false;

                break;
            }
            case 'PREFILL_WELCOME_BACK': {
                event = 'PAGE_VIEW_EVENT';
                const pageData = yield select(getPageViewData, event, ' welcome back');
                const ecommerceData = yield select(getEcommerceData, action.type);
                const prefill = yield select(getPrefillProgress);
                data = { ...pageData, ...ecommerceData, prefill };
                break;
            }
            case 'TRACK_LOADING_PAGE': {
                event = 'INTERACTION_EVENT';
                data = {
                    event,
                    interaction: {
                        ixn_action: 'view',
                        ixn_type: 'Journey Context',
                        ixn_object: 'loading page',
                        ixn_label: get(action, 'label', 'loading_page')
                    }
                };
                break;
            }
            case 'TRACK_LOADING_PAGE_API_RESULT': {
                event = 'INTERACTION_EVENT';
                data = {
                    event,
                    interaction: {
                        ixn_action: 'response',
                        ixn_type: 'Journey Context',
                        ixn_object: `loading page ${get(action, 'api', 'unknown')} result`,
                        ixn_label: get(action, 'result', 'unknown')
                    }
                };
                break;
            }
            default: {
                // Do not track
                return false;
            }
        }
        yield call(recordGTM, action, data);
        yield call(logMessage, `${event} pushed to GTM`, data);

        // Replay a PAGE_VIEW_EVENT that was deferred when SET_ROUTER_LOCATION fired before
        // journey transaction data was available (e.g. user arriving via an email deep-link).
        if (action.type === 'SET_JOURNEY_PAYLOAD' && _pendingPageView) {
            // Yield a macrotask so that synchronously-dispatched responseActions that follow
            // SET_JOURNEY_PAYLOAD in the same batch (e.g. TRACK_INITIAL_FORM on the start page)
            // have a chance to run and clear _pendingPageView. If cleared, the replay is skipped
            // because TRACK_INITIAL_FORM will fire the PAGE_VIEW_EVENT itself.
            yield call(() => new Promise(resolve => setTimeout(resolve, 0)));
            if (!_pendingPageView) return true;
            const pendingPageView = _pendingPageView;
            _pendingPageView = null;
            const replayTransaction = yield select(_getTransactionPayload);
            if (!replayTransaction) {
                _pendingPageView = pendingPageView;
                return true;
            }
            const replayData = { ...pendingPageView.data, transaction: replayTransaction };
            if (pendingPageView.pageName !== 'handover') {
                localStorage.setItem('trackingData', JSON.stringify(replayData));
            }
            yield call(recordGTM, { type: pendingPageView.actionType }, replayData);
            yield call(logMessage, 'PAGE_VIEW_EVENT pushed to GTM (replayed after journey load)', replayData);
        }
    } catch (error) {
        // Log the error using the reporter.
        yield call(reporterLog, action, error);
    }

    return true;
};
